// src/pages/Home.js
import React, { useState, useEffect, useRef } from 'react';
import placeholderImage from '../assets/images/placeholder.jpg';

// 🔧 Configura la base de imágenes (ajústala según tu servidor)
const IMAGE_BASE_URL = 'https://api.crokchef.com/api/media/'; // o la ruta que uses

const Home = ({
  cartItems,
  addToCart,
  updateQuantity,
  getTotalItems,
  getTotalPrice,
  isCartOpen,
  setIsCartOpen,
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(4);
  const sliderRef = useRef(null);

  // Detectar tamaño de pantalla
    useEffect(() => {
      const handleResize = () => {
          if (window.innerWidth < 768) {
          setItemsPerView(2);
          } else {
          setItemsPerView(4);
          }
      };
      handleResize(); // inicial
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Reiniciar índice cuando cambien los productos o itemsPerView
    useEffect(() => {
      setCurrentIndex(0);
    }, [products, itemsPerView]);

    const totalPages = Math.ceil(products.length / itemsPerView);
    const maxIndex = Math.max(0, totalPages - 1);

    const goToPrev = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
    };

    const goToNext = () => {
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
    };

  // ---------- CARGAR PRODUCTOS ----------
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.crokchef.com/api/productos/populares');
        if (!response.ok) throw new Error('Error al cargar productos');
        const json = await response.json();

        // ✅ La API devuelve { success, message, data: [...] }
        if (json.success && Array.isArray(json.data)) {
          setProducts(json.data);
        } else {
          throw new Error('Formato de respuesta inesperado');
        }
        setError(null);
      } catch (err) {
        console.error(err);
        setError('No pudimos cargar el menú. Intenta más tarde.');
        // Opcional: usar datos de respaldo para pruebas
        // setProducts(defaultProducts);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // ---------- ÚLTIMO PRODUCTO PARA EL HERO ----------
  const lastProduct = products.length > 0 ? products[products.length - 1] : null;

  // ---------- FUNCIÓN PARA OBTENER URL DE IMAGEN ----------
  const getImageUrl = (imagePath) => {
    if (!imagePath) return placeholderImage;
    // Si ya es una URL absoluta, devolverla tal cual
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // Si es relativa, concatenar con la base
    return `${IMAGE_BASE_URL}${imagePath}`;
  };

  // ---------- RENDER ----------
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🍔</div>
          <p className="text-lg font-semibold">Cargando menú...</p>
          <div className="mt-4 w-16 h-16 border-4 border-t-red-600 rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md p-8 bg-white rounded-2xl shadow-lg">
          <div className="text-6xl mb-4">😅</div>
          <h2 className="text-2xl font-bold text-red-600 mb-4">Oops!</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-red-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ====== HERO SECTION ====== */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundColor: 'var(--primary-color)' }}
      >
        <div
          className="absolute top-0 right-0 w-full md:w-1/2 h-full origin-top -skew-x-[20deg] md:right-[-10%]"
          style={{ backgroundColor: 'var(--secondary-color)' }}
        ></div>
        <div className="mx-auto max-w-7xl px-4 py-20 lg:py-32 relative z-10 flex flex-col md:flex-row items-center gap-12">
          <div className="flex-1 text-center md:text-left">
            <p
              className="inline-block font-bold px-4 py-1 rounded-full mb-6 text-sm uppercase tracking-wider"
              style={{ backgroundColor: 'var(--secondary-light)', color: 'var(--primary-hover)' }}
            >
              {lastProduct ? `Nuevo: ${lastProduct.nombre}` : 'Nuevo Combo Crispy'}
            </p>
            <h1
              className="text-4xl md:text-6xl font-black text-white italic leading-tight mb-6 uppercase"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Sabor que <span style={{ color: 'var(--secondary-light)' }}>ruge</span> en cada mordisco.
            </h1>
            <p className="text-lg text-white/90 mb-8 max-w-lg mx-auto md:mx-0">
              Pide tus hamburguesas favoritas y nuestro famoso pollo crujiente en línea
              y pásalo a buscar o te lo llevamos rápido a casa.
            </p>
            <a
              href="#menu"
              className="inline-flex items-center gap-3 font-bold px-8 py-4 rounded-full text-lg transition-all hover:scale-105 no-underline"
              style={{ backgroundColor: 'var(--secondary-color)', color: 'black' }}
            >
              Pide Ahora
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16" height="16">
                <path
                  fill="currentColor"
                  d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z"
                />
              </svg>
            </a>
          </div>
          <div className="flex-1 relative">
            {lastProduct && lastProduct.imagen_url ? (
              <img
                src={getImageUrl(lastProduct.imagen_url)}
                alt={lastProduct.nombre}
                className="w-full max-w-md mx-auto drop-shadow-2xl transition-transform duration-500 hover:scale-105 rounded-2xl"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <div class="w-full max-w-md mx-auto drop-shadow-2xl bg-gradient-to-br from-amber-100 to-orange-200 rounded-2xl overflow-hidden flex items-center justify-center text-8xl" style="aspect-ratio: 1/1;">
                      🍔
                    </div>
                  `;
                }}
              />
            ) : (
              <div className="w-full max-w-md mx-auto drop-shadow-2xl bg-gradient-to-br from-amber-100 to-orange-200 rounded-2xl overflow-hidden flex items-center justify-center text-8xl aspect-square">
                🍔
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ====== MENÚ SECTION ====== */}
      <section id="menu" className="py-20" style={{ backgroundColor: 'var(--background-color)' }}>
        <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="text-center mb-16">
            <h2
                className="text-3xl md:text-5xl font-black uppercase italic"
                style={{ color: 'var(--primary-hover)', fontFamily: 'var(--font-heading)' }}
            >
                Nuestro Menú
            </h2>
            <p className="mt-4 font-medium" style={{ color: 'var(--text-muted)' }}>
                Preparado al instante, con los mejores ingredientes.
            </p>
            </div>

            <div className="relative">
            {/* Contenedor del slider */}
            <div className="overflow-hidden rounded-3xl">
                <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{
                    transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`,
                    width: `${(products.length / itemsPerView) * 100}%`,
                }}
                >
                {products.map((product) => {
                    const finalPrice =
                    product.es_oferta && product.precio_oferta
                        ? parseFloat(product.precio_oferta)
                        : parseFloat(product.precio_base);
                    const isAvailable = product.disponible === true;

                    return (
                    <div
                        key={product.id}
                        className="px-3" // separación entre tarjetas
                        style={{
                        flex: `0 0 ${100 / itemsPerView}%`,
                        minWidth: 0,
                        }}
                    >
                        <div
                        className="rounded-3xl overflow-hidden shadow-sm border flex flex-col transition-all hover:-translate-y-1 hover:shadow-lg h-full"
                        style={{ backgroundColor: 'var(--surface-color)', borderColor: 'var(--border-color)' }}
                        >
                        <div className="relative pb-[75%] bg-neutral-100 flex justify-center items-center overflow-hidden">
                            <img
                            src={getImageUrl(product.imagen_url)}
                            alt={product.nombre}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                            loading="lazy"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = `
                                <div class="absolute inset-0 w-full h-full flex items-center justify-center text-6xl bg-gradient-to-br from-gray-100 to-gray-200">
                                    🍽️
                                </div>
                                <div class="absolute top-4 left-4 text-white text-xs font-bold px-3 py-1 rounded-full uppercase" style="background-color: var(--primary-color);">
                                    ${product.categoria?.nombre || 'Producto'}
                                </div>
                                `;
                            }}
                            />
                            {product.es_oferta && (
                            <div className="absolute top-4 right-4 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                                OFERTA
                            </div>
                            )}
                            {!isAvailable && (
                            <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                                <span className="text-white font-bold text-lg uppercase">No disponible</span>
                            </div>
                            )}
                            <div
                            className="absolute top-4 left-4 text-white text-xs font-bold px-3 py-1 rounded-full uppercase"
                            style={{ backgroundColor: 'var(--primary-color)' }}
                            >
                            {product.categoria?.nombre || 'General'}
                            </div>
                        </div>
                        <div className="p-6 flex flex-col flex-grow">
                            <h3 className="text-xl font-bold mb-2 leading-tight" style={{ color: 'var(--text-color)' }}>
                            {product.nombre}
                            </h3>
                            <p className="text-sm mt-1 flex-grow line-clamp-2" style={{ color: 'var(--text-muted)' }}>
                            {product.descripcion || 'Delicioso producto.'}
                            </p>
                            <div className="mt-6 flex items-center justify-between">
                            <div className="flex flex-col items-start">
                                <span className="text-2xl font-black" style={{ color: 'var(--primary-color)' }}>
                                ${finalPrice.toFixed(2)}
                                </span>
                                {product.es_oferta && product.precio_oferta && (
                                <span className="text-xs line-through text-gray-400">
                                    ${parseFloat(product.precio_base).toFixed(2)}
                                </span>
                                )}
                            </div>
                            <button
                                className={`btn btn-circle border-none text-black transition-all hover:scale-110 ${
                                !isAvailable ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                                style={{
                                backgroundColor: isAvailable ? 'var(--secondary-color)' : 'var(--border-color)',
                                }}
                                onClick={() => isAvailable && addToCart(product)}
                                disabled={!isAvailable}
                                aria-label={`Agregar ${product.nombre} al carrito`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="18" height="18">
                                <path
                                    fill="currentColor"
                                    d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 144L48 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l144 0 0 144c0 17.7 14.3 32 32 32s32-14.3 32-32l0-144 144 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-144 0 0-144z"
                                />
                                </svg>
                            </button>
                            </div>
                        </div>
                        </div>
                    </div>
                    );
                })}
                </div>
            </div>

            {/* Botones de navegación (solo si hay más de una página) */}
            {totalPages > 1 && (
                <>
                <button
                    onClick={goToPrev}
                    disabled={currentIndex === 0}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/80 shadow-lg hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
                    style={{ marginLeft: '-12px' }}
                    aria-label="Anterior"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" width="20" height="20">
                    <path fill="currentColor" d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"/>
                    </svg>
                </button>
                <button
                    onClick={goToNext}
                    disabled={currentIndex === maxIndex}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/80 shadow-lg hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
                    style={{ marginRight: '-12px' }}
                    aria-label="Siguiente"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" width="20" height="20">
                    <path fill="currentColor" d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"/>
                    </svg>
                </button>
                </>
            )}
            </div>
        </div>
        </section>

      {/* ====== NOSOTROS ====== */}
      <section id="nosotros" className="py-20" style={{ backgroundColor: 'var(--surface-color)' }}>
        <div className="mx-auto max-w-7xl px-4 md:px-6 text-center">
          <h2
            className="text-3xl md:text-5xl font-black uppercase italic"
            style={{ color: 'var(--primary-hover)', fontFamily: 'var(--font-heading)' }}
          >
            Sobre Nosotros
          </h2>
          <p className="mt-4 max-w-2xl mx-auto" style={{ color: 'var(--text-muted)' }}>
            En CrokChef nos apasiona la comida rápida de calidad. Usamos ingredientes frescos
            y recetas que hacen que cada mordisco sea una experiencia única.
          </p>
        </div>
      </section>

      {/* ====== CARRITO FLOTANTE ====== */}
      {isCartOpen && (
        <>
          {/* Fondo oscuro detrás del carrito */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => setIsCartOpen(false)}
          ></div>

          {/* Panel del carrito */}
          <div className="fixed right-0 top-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 overflow-y-auto transition-transform duration-300 animate-slide-in">
            <div className="flex flex-col h-full">
              {/* Encabezado */}
              <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <h2 className="text-2xl font-black uppercase italic" style={{ color: 'var(--primary-color)', fontFamily: 'var(--font-heading)' }}>
                  Tu Pedido
                </h2>
                <button
                  onClick={() => setIsCartOpen(false)}
                  className="btn btn-sm btn-circle btn-ghost cursor-pointer"
                  aria-label="Cerrar carrito"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" className="w-4 h-4">
                    <path fill="currentColor" d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z" />
                  </svg>
                </button>
              </div>

              {/* Lista de productos */}
              <div className="p-6 flex-grow overflow-y-auto">
                {cartItems.length === 0 ? (
                  <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
                    Tu carrito está vacío
                  </p>
                ) : (
                  cartItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex gap-4 items-center p-3 rounded-2xl border mb-4"
                      style={{ backgroundColor: 'white', borderColor: 'var(--border-color)' }}
                    >
                      {/* Imagen */}
                      <img
                        src={getImageUrl(item.imagen_url)}
                        alt={item.nombre || 'Producto'}
                        className="w-16 h-16 object-cover rounded-xl"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const parent = e.target.parentElement;
                          const fallback = document.createElement('div');
                          fallback.className = 'w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center text-3xl';
                          fallback.textContent = '🍽️';
                          parent.appendChild(fallback);
                        }}
                      />

                      {/* Nombre y precio */}
                      <div className="flex-grow">
                        <h4 className="font-bold text-sm leading-tight mb-1" style={{ color: 'var(--text-color)' }}>
                          {item.nombre || 'Producto sin nombre'}
                        </h4>
                        <span className="font-bold text-sm" style={{ color: 'var(--primary-color)' }}>
                          ${(item.precio || 0).toFixed(2)}
                        </span>
                      </div>

                      {/* Controles de cantidad */}
                      <div
                        className="flex flex-col items-center bg-neutral-50 rounded-lg overflow-hidden border"
                        style={{ borderColor: 'var(--border-color)' }}
                      >
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="px-3 py-1 hover:bg-[var(--secondary-color)] cursor-pointer text-xs bg-transparent border-none"
                          aria-label="Aumentar cantidad"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="12" height="12">
                            <path fill="currentColor" d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 144L48 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l144 0 0 144c0 17.7 14.3 32 32 32s32-14.3 32-32l0-144 144 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-144 0 0-144z" />
                          </svg>
                        </button>
                        <span className="font-bold text-sm py-1">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="px-3 py-1 hover:bg-neutral-200 cursor-pointer text-xs bg-transparent border-none"
                          aria-label="Disminuir cantidad"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="12" height="12">
                            <path fill="currentColor" d="M432 256c0 17.7-14.3 32-32 32L48 288c-17.7 0-32-14.3-32-32s14.3-32 32-32l352 0c17.7 0 32 14.3 32 32z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pie del carrito (total y formulario) */}
              <div className="p-6 border-t" style={{ borderColor: 'var(--border-color)', backgroundColor: '#f9fafb' }}>
                <div className="flex justify-between items-center mb-4 text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-2xl" style={{ color: 'var(--primary-color)' }}>
                    ${getTotalPrice().toFixed(2)}
                  </span>
                </div>
                {/*
                <form className="flex flex-col gap-3 border-t pt-4 mt-2" style={{ borderColor: 'var(--border-color)' }}>
                  <h3 className="font-bold mb-2">Tus Datos</h3>
                  <fieldset className="w-full">
                    <label className="text-xs font-semibold uppercase mb-1 block" style={{ color: 'var(--text-muted)' }}>
                      Nombre y Apellido
                    </label>
                    <input type="text" name="firstName" className="input w-full rounded-xl" required placeholder="Ej. Ana P." />
                  </fieldset>
                  <fieldset className="w-full">
                    <label className="text-xs font-semibold uppercase mb-1 block" style={{ color: 'var(--text-muted)' }}>
                      Email
                    </label>
                    <input type="email" name="email" className="input w-full rounded-xl" required placeholder="correo@ejemplo.com" />
                  </fieldset>
                  <fieldset className="w-full">
                    <label className="text-xs font-semibold uppercase mb-1 block" style={{ color: 'var(--text-muted)' }}>
                      Teléfono
                    </label>
                    <input type="tel" name="phone" className="input w-full rounded-xl" required placeholder="Tu celular" />
                  </fieldset>
                </form>
                  */}
                <button
                  className="btn w-full rounded-full text-lg font-semibold mt-3 text-white border-none transition-all hover:scale-[1.02]"
                  style={{ backgroundColor: 'var(--primary-color)' }}
                  onClick={() => alert('Pronto podrás pagar online')}
                >
                  Continuar al pago
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Home;